import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/primitives";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuth } from "@/lib/auth";
import { fileToSquareJpeg } from "@/lib/image";
import { ArrowLeft, ImagePlus, Save, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, updateProfile } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", garden_name: "", location: "" });
  const [avatar, setAvatar] = useState(null); // dataURL or null
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (user && user !== false) {
      setForm({
        name: user.name || "",
        garden_name: user.garden_name || "",
        location: user.location || "",
      });
      setAvatar(user.avatar_data_url || null);
    }
  }, [user]);

  if (user === null) return null;
  if (!user) return null;

  const pickAvatar = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const data = await fileToSquareJpeg(f, 480, 0.85);
      setAvatar(data);
    } catch (err) { toast.error(err.message || "Could not load image"); }
    finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Your name can't be empty"); return; }
    setSaving(true);
    try {
      await updateProfile({
        name: form.name.trim(),
        garden_name: form.garden_name.trim(),
        location: form.location.trim(),
        avatar_data_url: avatar || "",
      });
      toast.success("Profile saved");
      nav("/home");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="profile-page">
      <div className="pt-6">
        <Link to="/home" className="chip" data-testid="profile-back"><ArrowLeft size={14} /> Home</Link>
      </div>
      <PageHeader kicker="Your details" title="Profile" subtitle="A few things that travel with you across the patch" />

      <form onSubmit={save} className="h-card p-5 sm:p-6 space-y-4" data-testid="profile-form">
        {/* Avatar */}
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-full overflow-hidden bg-cream-deep border border-heritage-border flex items-center justify-center shrink-0" data-testid="profile-avatar-preview">
            {avatar
              ? <img src={avatar} alt="Avatar" className="w-full h-full object-cover" />
              : <BrandLogo size={64} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="kicker">Profile picture</div>
            <div className="text-sm text-ink-soft">A square photo, anything in the garden works.</div>
            <div className="mt-2 flex flex-wrap gap-2">
              <button type="button" onClick={() => fileRef.current?.click()} className="chip" data-testid="profile-avatar-upload" disabled={uploading}>
                {uploading ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />}
                {uploading ? "Loading..." : avatar ? "Change" : "Upload"}
              </button>
              {avatar && (
                <button type="button" onClick={() => setAvatar(null)} className="chip" data-testid="profile-avatar-remove">
                  <Trash2 size={13} className="text-terracotta" /> Remove
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickAvatar} />
            </div>
          </div>
        </div>

        <label className="block">
          <span className="text-sm text-ink-soft mb-1.5 inline-block">Your name</span>
          <input className="field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" required />
        </label>
        <label className="block">
          <span className="text-sm text-ink-soft mb-1.5 inline-block">Garden name</span>
          <input className="field" value={form.garden_name} onChange={(e) => setForm({ ...form, garden_name: e.target.value })} placeholder="e.g., The Mangaroa Patch" data-testid="profile-garden-name" />
        </label>
        <label className="block">
          <span className="text-sm text-ink-soft mb-1.5 inline-block">Where you garden</span>
          <input className="field" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g., Wellington, NZ" data-testid="profile-location" />
        </label>

        <div className="text-sm text-ink-soft border-t border-heritage-border pt-4">
          Signed in as <span className="font-display text-base text-sage-dark">{user.email}</span>
        </div>

        <div className="flex justify-end gap-2">
          <Link to="/home" className="btn-ghost" data-testid="profile-cancel">Cancel</Link>
          <button type="submit" className="btn-heritage" disabled={saving} data-testid="profile-save">
            <Save size={14} /> {saving ? "Saving..." : "Save profile"}
          </button>
        </div>
      </form>

      <ChangePassword />
    </div>
  );
}

function ChangePassword() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.current_password || !form.new_password) { setErr("Fill in both passwords"); return; }
    if (form.new_password.length < 6) { setErr("New password must be at least 6 characters"); return; }
    if (form.new_password !== form.confirm) { setErr("New passwords don't match"); return; }
    setBusy(true);
    try {
      const { apiClient } = await import("@/lib/auth");
      await apiClient.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not change password");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="h-card p-5 sm:p-6 space-y-3 mt-5" data-testid="change-password-form">
      <div className="kicker">Reset password</div>
      <p className="text-sm text-ink-soft">Choose a new password to keep your garden safe.</p>
      <label className="block">
        <span className="text-sm text-ink-soft mb-1.5 inline-block">Current password</span>
        <input type="password" className="field" value={form.current_password}
          onChange={(e) => setForm({ ...form, current_password: e.target.value })}
          autoComplete="current-password" data-testid="cp-current" required />
      </label>
      <label className="block">
        <span className="text-sm text-ink-soft mb-1.5 inline-block">New password</span>
        <input type="password" className="field" value={form.new_password}
          onChange={(e) => setForm({ ...form, new_password: e.target.value })}
          autoComplete="new-password" data-testid="cp-new" required />
      </label>
      <label className="block">
        <span className="text-sm text-ink-soft mb-1.5 inline-block">Confirm new password</span>
        <input type="password" className="field" value={form.confirm}
          onChange={(e) => setForm({ ...form, confirm: e.target.value })}
          autoComplete="new-password" data-testid="cp-confirm" required />
      </label>
      {err && <div className="bg-cream-deep border border-heritage-border rounded-xl px-4 py-3 text-sm text-terracotta-dark" data-testid="cp-error">{err}</div>}
      <div className="flex justify-end">
        <button type="submit" className="btn-heritage" disabled={busy} data-testid="cp-submit">
          {busy ? "Updating..." : "Update password"}
        </button>
      </div>
    </form>
  );
}
